/* eslint-disable array-callback-return */
import FrontLayout from "../../../components/layout-new-theme/FrontLayout";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useState } from "react";
import BookingInformation from "./_components/BookingInformation";
import { useDispatch, useSelector } from "react-redux";
import {
  addToCartReduxStore,
  updateBooking,
  addByTheHourFormToState,
  addSpecifiedDestinationFormToState,
  checkBookingPayment,
  updateCartCustomer,
  updateStatusAuthenticatedUserBooking
} from "../../../actions/admin/booking";
import {
  submitBooking
} from "../../../actions/admin/bookingv2";
import moment from "../../../utils/customMoment";
import jwtDecode from "jwt-decode";
import { useLoading } from "../../../components/LoadingContext";
import AlertModal from "../discover/_components/AlertModal";
import ConfirmationModal from "./_components/ConfirmationModal";
import { pushEventGTM } from "../../../utils/gtm";
import TotalCard from "../../../components/total-card";
import VehicleInformation from "./_components/VehicleInformation";
import { ArrowRightIcon, BackAltIcon } from "../../../components/icons";
import { motion } from "framer-motion";
import { Reveal, fadeSide, pressable } from "../../../components/motion";
import ReactTooltip from "react-tooltip";
import { getBookingPriceBreakdown } from "../../../utils/discount";
import { applyCartHold } from "../../../utils/cartExpiry";

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_CART_EMAIL = "cart@email.com";
const DEFAULT_CART_USER = "cart user";
const DEFAULT_CART_PHONE = "0000000000";
const DEFAULT_CART_ADDRESS = "cart address";
const DEFAULT_POSTAL_CODE = "0000";
const DEFAULT_CITY = "Tokyo";
const DEFAULT_PREFIX_NUMBER = "+81";
const DEFAULT_COUNTRY = "Japan";
const DEFAULT_COUNTRY_CODE = "JP";
const ERROR_MESSAGE = "Oops, something went wrong on our end.";

// ============================================================================
// HELPER FUNCTIONS - GTM Tracking
// ============================================================================

/**
 * Creates a GTM item object for a single booking item
 * @param {Object} bookingItem - The booking item
 * @param {number} index - The index of the item
 * @param {Object} cartItem - The cart item containing booking details
 * @returns {Object} GTM item object
 */
const createGTMItem = (bookingItem, index, cartItem) => {
  const totalChildSeats = bookingItem.childSeat[0].childSeatCount +
    bookingItem.childSeat[1].childSeatCount +
    bookingItem.childSeat[2].childSeatCount;

  const itemCategory = cartItem.bookingType === "byTheHour"
    ? "By The Hour"
    : cartItem.isAirportTransfer
      ? "Airport Transfer"
      : "Specified Destination";

  const itemCategory2 = cartItem.destinations.length === 1
    ? cartItem.destinations[0].place_id
    : cartItem.destinations[cartItem.destinations.length - 1].place_id;

  const itemCategory3 = totalChildSeats > 0 ? "Child Seats" : "";

  return {
    index,
    item_id: bookingItem.vehicleTypeID,
    item_name: bookingItem.vehicleTypeName,
    item_brand: "Toyota",
    affiliation: "Flywire",
    coupon: "",
    discount: 0,
    item_category: itemCategory,
    item_category2: itemCategory2,
    item_category3: itemCategory3,
    location_id: "",
    price: bookingItem.total,
    quantity: 1,
  };
};

/**
 * Creates GTM ecommerce data for a cart item
 * @param {Object} cartItem - The cart item
 * @returns {Object} GTM ecommerce data
 */
const createGTMEcommerceData = (cartItem) => {
  const items = cartItem.bookingInfo.map((bookingItem, index) =>
    createGTMItem(bookingItem, index, cartItem)
  );

  return {
    currency: "JPY",
    value: cartItem.totalPrice,
    items,
  };
};

/**
 * Pushes a GTM event with ecommerce data
 * @param {string} eventName - The name of the GTM event
 * @param {Object} cartItem - The cart item
 */
const pushGTMEvent = (eventName, cartItem) => {
  if (cartItem) {
    const ecommerceData = createGTMEcommerceData(cartItem);
    pushEventGTM({
      event: eventName,
      ecommerce: ecommerceData,
    });
  }
};

// ============================================================================
// HELPER FUNCTIONS - Cart ID Generation
// ============================================================================

/**
 * Generates a random cart ID
 * @returns {string} Generated cart ID
 */
const generateCartId = () => {
  return Math.floor(Math.random() * 16777215).toString(16) +
    Math.floor(Math.random() * 1000 + 1);
};

/**
 * Gets or generates a cart ID from local storage
 * @param {Array|null} localStorageCart - The cart from local storage
 * @returns {string} Cart ID
 */
const getOrGenerateCartId = (localStorageCart) => {
  if (localStorageCart !== null && localStorageCart.length > 0) {
    if (localStorageCart[0].cartID) {
      return localStorageCart[0].cartID;
    }
  }
  return generateCartId();
};

// ============================================================================
// HELPER FUNCTIONS - User Authentication
// ============================================================================

/**
 * Checks if the user token exists and is valid
 * @returns {Promise<Object|null>} Decoded JWT or null
 */
const validateUserToken = async () => {
  const token = localStorage.getItem("token");
  if (!token) return null;

  const decodedJwt = jwtDecode(token);
  const isValidUser =
    (decodedJwt.user.id.role === "individual" ||
      decodedJwt.user.id.role === "business") &&
    decodedJwt.user.id.status === "ACTIVE";

  return isValidUser ? decodedJwt : null;
};

/**
 * Gets default account details for non-authenticated users
 * @returns {Object} Default account details
 */
const getDefaultAccountDetail = () => ({
  billingInformation: {
    name: DEFAULT_CART_USER,
    prefix_number: DEFAULT_PREFIX_NUMBER,
    phone: DEFAULT_CART_PHONE,
    country_code: DEFAULT_COUNTRY_CODE,
    country: DEFAULT_COUNTRY,
    address: DEFAULT_CART_ADDRESS,
    postal_code: DEFAULT_POSTAL_CODE,
    city: DEFAULT_CITY,
  },
});

/**
 * Extracts account details from decoded JWT
 * @param {Object} decodedJwt - The decoded JWT token
 * @returns {Object} Account details
 */
const extractAccountDetail = (decodedJwt) => {
  const role = decodedJwt.user.id.role;
  const detailKey = role === "individual" ? "individualDetail" : "businessDetail";
  return decodedJwt?.user?.id[detailKey];
};

// ============================================================================
// HELPER FUNCTIONS - Trip Sorting
// ============================================================================

/**
 * Compares two trip objects based on tripStartTime
 * @param {Object} a - First trip object
 * @param {Object} b - Second trip object
 * @returns {number} Comparison result (-1, 0, or 1)
 */
const compareTrips = (a, b) => {
  const startTimeA = moment(a.tripStartTime);
  const startTimeB = moment(b.tripStartTime);

  if (startTimeA.isBefore(startTimeB)) {
    return -1;
  } else if (startTimeA.isAfter(startTimeB)) {
    return 1;
  } else {
    return 0;
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const BookingConfirmationPage = (props) => {
  const { showLoading, closeLoading } = useLoading();
  const dispatch = useDispatch();

  // Extract props and initialize state
  const cart_item_raw = props.location.state?.cart_item || "";
  const isEditBooking = props.location.state?.isEditBooking || false;
  const keyBook = props.location.state?.keyBook || null;
  const [cartItem, setCartItem] = useState(JSON.parse(cart_item_raw));
  const { t, i18n } = useTranslation();
  // Set moment locale based on language
  if (i18n.language.toUpperCase() === "EN") {
    moment.locale("en");
  } else {
    moment.locale("ja");
  }
  const history = useHistory();
  const pickupDateTime = new Date(cartItem.pickupDateTime);
  const [showModalAlert, setShowModalAlert] = useState(false);
  const [modalAlertText, setmodalAlertText] = useState("");
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);
  const [isRecurring, setIsRecurring] = useState(false);
  const [inputFields, setInputFields] = useState([""]);
  const [inputFieldsToShow, setInputFieldsToShow] = useState([""]);
  const [inputFieldsToShowFormated, setInputFieldsToShowFormated] = useState([""]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleCloseModalAlert = () => {
    setShowModalAlert(false);
  };

  const handleOnChangeCartItem = (item) => {
    setCartItem({
      ...item,
      totalPrice: getTotalPriceFromCart(item),
    });
  };

  // ============================================================================
  // CART PRICE CALCULATION
  // ============================================================================

  /**
   * Calculates the total price from a cart item — discount included, so it matches the
   * price card and the amount the backend will charge.
   * @param {Object} item - The cart item
   * @returns {number} Total price
   */
  const getTotalPriceFromCart = (item) => getBookingPriceBreakdown(item).totalPrice;

  // ============================================================================
  // AUTHENTICATION FUNCTIONS
  // ============================================================================

  /**
   * Checks if the current user is authenticated and eligible for deferred payment
   * @returns {Promise<boolean>} True if eligible for deferred payment
   */
  const checkIsAuth = async () => {
    const token = localStorage.getItem("token");
    if (!token) return false;

    const decodedJwt = jwtDecode(token);
    const isBusinessUser = decodedJwt.user.role === "business";
    const isDeferredPaymentEligible = decodedJwt.user.id.deferredPaymentEligible;

    return token && isBusinessUser && isDeferredPaymentEligible;
  };

  /**
   * Gets authentication information from the JWT token
   * @returns {Promise<Object>} Authentication information
   */
  const getAuthInfo = async () => {
    const token = localStorage.getItem("token");
    const decodedJwt = jwtDecode(token);
    const accountDetail = extractAccountDetail(decodedJwt);
    const billingInfo = accountDetail.billingInformation;

    return {
      pronounced: billingInfo.pronounced,
      name: billingInfo.name,
      email: decodedJwt.user.id.email,
      phone: billingInfo.phone,
      address: billingInfo.address,
      postCode: billingInfo.postal_code,
      country: billingInfo.country,
      country_code: billingInfo.country_code,
      userId: decodedJwt.user.id._id,
      prefix_number: billingInfo.prefix_number,
      role: decodedJwt.user.role,
    };
  };

  /**
   * Checks if the user token is valid
   * @returns {Promise<Object|null>} Decoded JWT or null
   */
  const checkTokenUser = async () => {
    return await validateUserToken();
  };

  // ============================================================================
  // PAYMENT FUNCTIONS
  // ============================================================================

  /**
   * Submits a deferred payment booking for authenticated business users
   * @param {Array} cart - Array of cart items
   */
  const submitDeferredPaymentBooking = async (cart) => {
    const checkBookingPaymentResult = await checkBookingPayment(cart[0].cartID);

    if (checkBookingPaymentResult.status === "ELIGIBLE") {
      const authInfo = await getAuthInfo();

      await updateCartCustomer(cart, authInfo);

      const responseUpdate = await updateStatusAuthenticatedUserBooking({
        cart: cart,
        customerInfo: authInfo,
        statusBooking: "CONFIRMED",
        statusPayment: "CHARGE_PENDING",
      });

      // Push GTM events for each cart item
      cart.map((cartItem) => {
        pushGTMEvent("add_payment_info", cartItem);
      });

      closeLoading(true);
      if (responseUpdate.status === 200) {
        history.push(`/PaymentSuccess/${cart[0].cartID}`);
      }
    }
  };

  // ============================================================================
  // CART MANAGEMENT FUNCTIONS
  // ============================================================================

  /**
   * Adds the current cart item to the cart and redirects
   * @param {string} redirect - The redirect path after adding to cart
   */
  const addToCart = async (redirect = "/Cart") => {
    showLoading(true);

    // Push GTM event
    pushGTMEvent("add_to_cart", cartItem);

    // Get or generate cart ID
    const localStoreItem = JSON.parse(localStorage.getItem("cart"));
    cartItem.cartID = getOrGenerateCartId(localStoreItem);

    // Store booking
    const storeBooking = await addItemCartToStore(cartItem);

    if (storeBooking.code !== 200) {
      closeLoading(true);
      console.error("Error store booking");
      setmodalAlertText(ERROR_MESSAGE);
      setShowModalAlert(true);
    } else {
      await resetForm();
      closeLoading(true);
      history.push(redirect);
    }
  };

  /**
   * Adds multiple cart items (for duplicate bookings)
   * @param {Array} arrCart - Array of cart items to add
   */
  const addToCartDuplicate = async (arrCart) => {
    showLoading(true);

    const localStoreItem = JSON.parse(localStorage.getItem("cart"));
    const cartId = getOrGenerateCartId(localStoreItem);

    await Promise.all(
      arrCart.map(async (item) => {
        item.cartID = cartId;
        const storeBooking = await addItemCartToStore(item);

        if (storeBooking.code !== 200) {
          closeLoading(true);
          console.error("Error store booking");
          setmodalAlertText(ERROR_MESSAGE);
          setShowModalAlert(true);
        }
      })
    );

    closeLoading(true);
    history.push("/Cart");
  };

  /**
   * Submits the checkout directly without adding to cart
   */
  const submitCheckout = async () => {
    showLoading(true);

    // Push GTM event
    pushGTMEvent("add_to_cart", cartItem);

    // Generate new cart ID
    const cartId = generateCartId();
    cartItem.cartID = cartId;

    // Store booking without adding to local storage cart
    const storeBooking = await addItemCartToStore(cartItem, false);

    if (storeBooking.code !== 200) {
      closeLoading(true);
      console.error("Error store booking");
      setmodalAlertText(ERROR_MESSAGE);
      setShowModalAlert(true);
    } else {
      await resetForm();

      let arrCart = [cartItem];

      // Check if user is eligible for deferred payment
      const isAuth = await checkIsAuth();
      if (isAuth) {
        await submitDeferredPaymentBooking(arrCart);
      } else {
        closeLoading(true);
        history.push("/checkout", {
          items_to_checkout: JSON.stringify(arrCart),
          fromCart: false,
        });
      }
    }
  };

  // ============================================================================
  // STORAGE FUNCTIONS
  // ============================================================================

  /**
   * Manages cart storage in local storage, Redux state, and database
   * @param {Object} item - The cart item to store
   * @param {boolean} storeLocalStorage - Whether to store in local storage
   * @returns {Promise<Object>} Result object with code and message
   */
  const addItemCartToStore = async (item, storeLocalStorage = true) => {
    try {
      item.totalPrice = getTotalPriceFromCart(item);

      const submitBookingResult = await addCartToDB(item, storeLocalStorage);

      if (submitBookingResult.code === 201) {
        if (storeLocalStorage) {
          const localStoreItem = JSON.parse(localStorage.getItem("cart"));

          if (localStoreItem !== null && localStoreItem.length > 0) {
            let storeCart;

            // Remove old booking if editing
            if (isEditBooking) {
              storeCart = localStoreItem.filter(obj => obj.key !== keyBook);
            } else {
              storeCart = localStoreItem;
            }

            storeCart.push(submitBookingResult.data);

            // Adding restarts the hold on the whole cart, so the countdown goes
            // back to the full window. The server already did this to every row
            // sharing the cartID; copy the new row's anchor over the items in
            // localStorage so the browser agrees. Editing is not adding -- it
            // goes through a different endpoint that leaves the hold alone, so
            // the client must not restart it either.
            const heldCart = isEditBooking
              ? storeCart
              : applyCartHold(storeCart, submitBookingResult.data);

            // Sort cart by trip start time
            const sortedCart = heldCart.sort(compareTrips);

            localStorage.setItem("cart", JSON.stringify(sortedCart));
            dispatch(addToCartReduxStore(sortedCart));

            if (!isEditBooking) {
              localStorage.setItem("lastAddedBookToCart", moment().toISOString());
            }
          } else {
            // Create new cart array
            const cartArrayStore = [submitBookingResult.data];
            localStorage.setItem("cart", JSON.stringify(cartArrayStore));
            dispatch(addToCartReduxStore(cartArrayStore));

            localStorage.setItem("lastAddedBookToCart", moment().toISOString());
          }
        }

        return {
          code: 200,
          desc: "Success",
        };
      } else {
        return {
          code: 500,
          msg: "INTERNAL_SERVER_ERROR",
        };
      }
    } catch (err) {
      return {
        code: 500,
        msg: "INTERNAL_SERVER_ERROR",
      };
    }
  };

  /**
   * Inserts cart data to the database
   * @param {Object} item - The cart item
   * @param {boolean} withCart - Whether to add to cart or mark as ready to checkout
   * @returns {Promise<Object>} Result object
   */
  const addCartToDB = async (item, withCart) => {
    const decodedJwtToken = await checkTokenUser();

    let accountDetail;
    let email;

    if (decodedJwtToken !== null) {
      accountDetail = extractAccountDetail(decodedJwtToken);
      email = decodedJwtToken.user.id.email;
    } else {
      email = DEFAULT_CART_EMAIL;
      accountDetail = getDefaultAccountDetail();
    }

    const billingInfo = accountDetail.billingInformation;
    const cartUser = billingInfo.name;
    const cartEmail = email;
    const cartPhone = billingInfo.phone;
    const cartAddress = billingInfo.address;
    const cartCountry = billingInfo.country;
    const cartPrefixNumber = billingInfo.prefix_number;
    const cartCountryCode = billingInfo.country_code;
    const cartPostCode = billingInfo.postal_code;
    const userId = decodedJwtToken !== null ? decodedJwtToken.user.id._id : "";

    if (isEditBooking) {
      dispatch(
        updateBooking(
          keyBook,
          item.travelDateWithPickupTime,
          item.pickuptime,
          item.pickuplocation,
          item.destinations,
          item.paxList,
          item.luggagelist,
          item.pickupFlightNo,
          item.dropoffFlightNo,
          item.isFromAirport,
          item.cutomerActualDropOffTime,
          item.dropoffDatetime,
          item.garageArivalTime,
          item.cutomerChangedDropOffTimeOnly,
          cartUser,
          cartEmail,
          cartPhone,
          cartAddress,
          cartCountry,
          cartCountryCode,
          cartPrefixNumber,
          cartPostCode,
          0,
          item.availableVehicleId,
          null,
          item.bookingInfo,
          withCart ? "ADDED_TO_CART" : "READY_TO_CHECKOUT",
          item.totalDistance,
          item.tripStartTime,
          item.garageArivalTimeWithAdditionalTime,
          item.note_arr,
          item.randomId,
          item.activePackage,
          item.passengerName === undefined ? "(your name)" : item.passengerName,
          item.specialRequest,
          item.meetAssist,
          item.byTheHourBook,
          item.duration,
          item.isManyPeople,
          item.totalPrice,
          item.childSeat,
          item.isAirportTransfer,
          userId,
          withCart,
          "Mr.",
          item.pronounced,
          "+81",
          false,
          item?.flightTracking
        )
      );
    } else {
      const submitRequest = {
        key: item.key,
        cartID: item.cartID ? item.cartID : item.randomId,
        travelDate: item.travelDate,
        pickupTime: item.pickupTime,
        pickupLocation: item.pickupLocation.value,
        destinations: item.destinations.map(dest => dest.place_id),
        pax: item.paxList,
        luggage: item.luggagelist,
        bookingType: item.bookingType,
        duration: item.bookingType === "byTheHour" ? item.duration : null,
        travelArea: item.bookingType === "byTheHour" ? item.travelArea : null,
        isAirportTransfer: item.isAirportTransfer || item.isFromAirport || item.isToAirport,
        isFromAirport: item.isFromAirport,
        isToAirport: item.isToAirport,
        isStationTransfer: item.isStationTransfer,
        isFromStation: item.isFromStation,
        isToStation: item.isToStation,
        meetAssist: item.meetAssist,
        isManyPeople: item.isManyPeople,
        bookingInfo: item.bookingInfo,
        passengerName: item.passengerName === undefined ? "(your name)" : item.passengerName,
        passengerPrefixNumber: item.passengerPrefixNumber === undefined ? "+81" : item.passengerPrefixNumber,
        passengerCountryCode: item.passengerCountryCode === undefined ? "JP" : item.passengerCountryCode,
        passengerCountry: item.passengerCountry === undefined ? "Japan" : item.passengerCountry,
        passengerPhoneNumber: item.passengerPhoneNumber === undefined ? "000" : item.passengerPhoneNumber,
        passengerPronounced: item.passengerPronounced,
        noteList: item.note_arr,
        pickupFlightNo: item.pickupFlightNo,
        pickupTerminal: item.pickupTerminal,
        dropoffFlightNo: item.dropoffFlightNo,
        dropoffTerminal: item.dropoffTerminal,
        pickupSign: item.pickupSign,
        pickupSignHonorific: item.pickupSignHonorific,
        userId: userId,
        // Only sent for "Book this transfer only", which skips the cart entirely.
        // PaymentSuccess wipes the stored cart when the paid booking says it came
        // from one, so a standalone booking has to say it did not -- otherwise it
        // clears a cart it was never part of. Left off for cart adds so the field
        // keeps its schema default, and with it the bookingStatus the admin list
        // view filters on.
        ...(withCart === false ? { withCart: false } : {}),
      };

      // Add referral code if available
      const referralCodeRaw = localStorage.getItem("referral_code");
      if (referralCodeRaw) {
        const { code } = JSON.parse(referralCodeRaw);
        submitRequest.referralCode = code;
      }

      const submitRes = await submitBooking(submitRequest);

      if (submitRes.statusCode != 201) {
        return {
          code: 500,
          message: "Error",
        };
      }

      return {
        code: 201,
        message: "Success",
        data: submitRes.data,
      };
    }
  };

  /**
   * Resets the booking form state
   */
  const resetForm = async () => {
    dispatch(addSpecifiedDestinationFormToState(null));
    dispatch(addByTheHourFormToState(null));
  };

  const shiftDateByDays = (dateString, days, format = "YYYY-MM-DDTHH:mm:ss.SSS[Z]") => {
    return moment(dateString).add(days, "days").format(format);
  };

  const shiftCartDates = (baseCart, targetCart, diffDays) => {
    const clone = { ...targetCart };
    clone.key = Math.floor(Math.random() * 1e10);

    clone.pickupDateTime = shiftDateByDays(baseCart.pickupDateTime, diffDays);
    clone.travelDate = shiftDateByDays(baseCart.travelDate, diffDays, "YYYY-MM-DD");
    clone.customerDropoffDateTime = shiftDateByDays(baseCart.customerDropoffDateTime, diffDays);
    clone.startFromGarage = shiftDateByDays(baseCart.startFromGarage, diffDays);
    clone.garageArrivalTime = shiftDateByDays(baseCart.garageArrivalTime, diffDays);

    return clone;
  };

  const duplicateBooking = () => {
    const result = [cartItem];

    inputFields.forEach((value) => {
      if (!value) return;

      const targetDate = moment(value);
      const baseDate = moment(cartItem.pickupDateTime).format("YYYY-MM-DD");
      const diffDays = targetDate.diff(baseDate, "days");

      const duplicated = JSON.parse(JSON.stringify(cartItem));
      result.push(shiftCartDates(cartItem, duplicated, diffDays));
    });

    addToCartDuplicate(result);
  };

  const isConfirmBookingDisabled = (cartItem) => {
    if (!cartItem) return true;

    const hasInvalidPassengerName = !cartItem.passengerName || cartItem.passengerName.trim() === "";

    if (hasInvalidPassengerName) return true;

    if (
      (cartItem.isFromAirport || cartItem.isFromStation) && (!cartItem.pickupFlightNo || cartItem.pickupFlightNo.trim() === "")
    ) {
      return true;
    }

    if (
      (cartItem.isToAirport || cartItem.isToStation) && (!cartItem.dropoffFlightNo || cartItem.dropoffFlightNo.trim() === "")
    ) {
      return true;
    }

    if (isRecurring && inputFields[0] === "") {
      return true;
    }

    return false;
  };

  const isConfirmEditBookingDisabled = (cartItem) => {
    if (!cartItem.passengerName || cartItem.passengerName === "") {
      return true;
    }

    if (cartItem.isAirportTransfer || cartItem.flightTracking) {
      if (
        cartItem.isFromAirport &&
        (!cartItem.pickupFlightNo || cartItem.pickupFlightNo === "")
      ) {
        return true;
      }
    }

    return false;
  };

  const getTooltipWording = (cartItem) => {
    const REQUIRED = "BookingConfirmation.ConfirmBookingDisableTooltip.RequiredForm";
    const DUPLICATE = "BookingConfirmation.ConfirmBookingDisableTooltip.DuplicateForm";

    const isEmpty = (v) => !v || v.trim() === "";

    const rules = [
      () => !cartItem,
      () => isEmpty(cartItem?.passengerName),
      () =>
        (cartItem?.isFromAirport || cartItem?.isFromStation) &&
        isEmpty(cartItem?.pickupFlightNo),
      () =>
        (cartItem?.isToAirport || cartItem?.isToStation) &&
        isEmpty(cartItem?.dropoffFlightNo),
    ];

    if (rules.some((rule) => rule())) {
      return REQUIRED;
    }

    if (isRecurring && isEmpty(inputFields?.[0])) {
      return DUPLICATE;
    }

    return REQUIRED;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <FrontLayout>
      <AlertModal
        handleClose={handleCloseModalAlert}
        show={showModalAlert}
        text={modalAlertText}
      />
      <div className="tw:!bg-[#FFFFFF] tw:md:!bg-[#F6F6F6]">
        <div className="tw:w-full tw:mx-auto tw:[--gx:1.5rem] tw:px-[calc(var(--gx)/2)] tw:mt-1 tw:pt-1">
          <div className="custom-mx">
            <div className="tw:pb-12 tw:!mt-8 tw:px-[calc(var(--gx)/2)] tw:lg:w-full tw:lg:flex-none">
              {/* The page arrives in reading order: back link, title, the form,
                  then the price summary beside it. */}
              <Reveal
                onMount
                variants={fadeSide(-12)}
                className="tw:flex tw:justify-start tw:!items-center tw:mb-3 tw:cursor-pointer tw:gap-2"
              >
                <BackAltIcon aria-label="Back icon" role="img" />
                <p
                  className="fw-100 tw:text-left tw:text-dark noto tw:inline tw:!m-0"
                  onClick={() => history.goBack()}
                >
                  {t("BookingConfirmation.BackToVehicleSelection")}
                </p>
              </Reveal>

              <Reveal
                onMount
                y={16}
                delay={0.05}
                className="tw:font-libre-caslon-display tw:text-[26px] tw:leading-[40px] tw:!font-[400] tw:mb-3"
              >
                {t("BookingConfirmation.Title")}
              </Reveal>

              <div className="tw:flex tw:flex-col tw:md:flex-row tw:justify-between tw:gap-0 tw:md:gap-3 tw:lg:gap-3">
                {/* VEHICLE INFORMATION MOBILE */}
                <Reveal onMount y={20} delay={0.1} className={`tw:w-full tw:block tw:md:hidden`}>
                  <VehicleInformation
                    cart={cartItem}
                    lang={i18n.language.toUpperCase()}
                    className="tw:-mx-5"
                  />
                </Reveal>

                {/* BookingInformation */}
                <Reveal onMount y={24} delay={0.12} className={`tw:w-full tw:md:w-2/3`}>
                  <BookingInformation
                    cart={cartItem}
                    removeItemFromCart={() => { }}
                    updateCartItem={handleOnChangeCartItem}
                    addToCartDuplicate={addToCartDuplicate}
                    checkTokenUser={checkTokenUser}
                    isEditBooking={isEditBooking}
                    addToCart={addToCart}
                    setShowModalConfirmation={setShowModalConfirmation}
                    lang={i18n.language.toUpperCase()}
                    duplicateBooking={duplicateBooking}
                    isRecurring={isRecurring}
                    setIsRecurring={setIsRecurring}
                    inputFields={inputFields}
                    setInputFields={setInputFields}
                    inputFieldsToShow={inputFieldsToShow}
                    setInputFieldsToShow={setInputFieldsToShow}
                    inputFieldsToShowFormated={inputFieldsToShowFormated}
                    setInputFieldsToShowFormated={setInputFieldsToShowFormated}
                    isConfirmEditBookingDisabled={isConfirmEditBookingDisabled}
                    isConfirmBookingDisabled={isConfirmBookingDisabled}
                    getTooltipWording={getTooltipWording}
                  />
                </Reveal>

                {/* Right Section DESKTOP */}
                <Reveal onMount y={24} delay={0.22} className={`tw:w-full tw:md:w-1/3`}>
                  <VehicleInformation
                    cart={cartItem}
                    lang={i18n.language.toUpperCase()}
                    className="tw:hidden tw:md:block tw:lg:block"
                  />

                  <TotalCard
                    cart={cartItem}
                    lang={i18n.language.toUpperCase()}
                    className="tw:mt-0 tw:lg:mt-4 tw:md:mt-4 tw:-mx-5 tw:md:mx-0 tw:mb-4"
                  />

                  <div
                    className="tw:!flex tw:!gap-2 tw:!block tw:md:!hidden"
                    data-tip
                    data-for={"disableButtonTooltip"}
                  >
                    <motion.button
                      type="button"
                      whileTap={pressable.tap}
                      className={`tw:inline-block tw:font-normal tw:leading-normal tw:text-center tw:no-underline tw:align-middle tw:cursor-pointer tw:select-none tw:border tw:border-transparent tw:px-3 tw:py-[0.375rem] tw:text-base tw:rounded-[0.25rem] tw:transition-[color,background-color,border-color,box-shadow] tw:duration-150 tw:disabled:opacity-65 tw:disabled:pointer-events-none tw:!bg-[#DBBE78] tw:!rounded tw:!py-2 tw:w-full tw:!w-6/6 tw:md:!w-6/6 `}
                      onClick={() => {
                        if (isEditBooking) {
                          addToCart("/Cart")
                        } else {
                          if (isRecurring) {
                            duplicateBooking();
                          } else {
                            setShowModalConfirmation(true);
                          }
                        }
                      }}
                      disabled={isEditBooking ? isConfirmEditBookingDisabled(cartItem) : isConfirmBookingDisabled(cartItem)}
                    >
                      <div className="tw:!flex tw:!items-center tw:!justify-center tw:!gap-2 tw:!text-[#FFFFFF] tw:!text-[15px] tw:!font-[700]">
                        {t("BookingConfirmation.ConfirmBooking")}
                        <ArrowRightIcon color="#FFFFFF" size={20} />
                      </div>
                    </motion.button>
                    {
                      isConfirmBookingDisabled(cartItem) && (
                        <ReactTooltip
                          id="disableButtonTooltip"
                          backgroundColor="#4A4A4A"
                          textColor="#FFFFFF"
                          data-tooltip-place="top"
                          data-tooltip-position-strategy="fixed"
                        >
                          <span
                            style={{ textAlign: "center", display: "block" }}
                            dangerouslySetInnerHTML={{
                              __html: t(getTooltipWording(cartItem)),
                            }}
                          />
                        </ReactTooltip>

                      )
                    }
                  </div>
                </Reveal>
              </div>

              <ConfirmationModal
                show={showModalConfirmation}
                onClose={() => setShowModalConfirmation(false)}
                cartItem={cartItem}
                pickupDateTime={pickupDateTime}
                lang={i18n.language.toUpperCase()}
                addToCart={addToCart}
                submitCheckout={submitCheckout}
              />
            </div>
          </div>
        </div>
      </div>
    </FrontLayout>
  );
};

export default BookingConfirmationPage;
